import type {
  SettingsDTO,
  SettingsUpdateInput,
} from "@/modules/settings/domain/contracts";
import { compressToWebP } from "@/shared/lib/image-compress";
import { uploadApi } from "@/shared/lib/upload-api";
import { uploadFileToPresignedUrl } from "@/shared/lib/upload-to-presigned";
import { ImageKeyUploadField } from "@/shared/ui/ImageKeyUploadField";
import {
  Button,
  FormInput,
  FormRoot,
  FormSwitch,
  FormTextarea,
  RHF,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  zodResolver,
} from "@devhop/ui";
import { CreditCard, Truck } from "lucide-react";
import { useCallback, useState } from "react";
import type { Resolver, UseFormReturn } from "react-hook-form";
import { z } from "zod";

const OG_KEY_PREFIX = "uploads/settings";

function sanitizeFilename(name: string): string {
  return name.replace(/[^a-zA-Z0-9._-]/g, "_").slice(0, 100) || "image";
}

/** อัปโหลดไฟล์ OG เป็น WebP (compress + presign + PUT) — เรียกตอน submit */
async function uploadOgImageToKey(file: File): Promise<string> {
  const blob = await compressToWebP(file);
  const baseName = file.name.includes(".")
    ? file.name.slice(0, file.name.lastIndexOf("."))
    : file.name;
  const key = `${OG_KEY_PREFIX}/${Date.now()}-${sanitizeFilename(baseName)}.webp`;
  const contentType = "image/webp";
  const { uploadUrl } = await uploadApi.getPresignUrl({ key, contentType });
  await uploadFileToPresignedUrl(uploadUrl, blob, { contentType });
  return key;
}

const I18nFormSchema = z.object({
  lo: z.string(),
  th: z.string(),
  en: z.string(),
});

const SocialLinksFormSchema = z.object({
  facebook: z.string(),
  tiktok: z.string(),
  instagram: z.string(),
  twitter: z.string(),
  youtube: z.string(),
});

const FormSchema = z.object({
  general: z.object({
    logoKey: z.string(),
    siteName: I18nFormSchema,
    slogan: I18nFormSchema,
    address: I18nFormSchema,
    phone: z.string(),
    email: z.string(),
    socialLinks: SocialLinksFormSchema,
  }),
  seo: z.object({
    title: I18nFormSchema,
    description: I18nFormSchema,
    imageKey: z.string(),
  }),
  codEnabled: z.boolean(),
  phajayEnabled: z.boolean(),
});

export type SettingsFormValues = z.output<typeof FormSchema>;

type SettingsFormProps = {
  initialValues: SettingsDTO;
  onSubmit: (input: SettingsUpdateInput) => void;
  onCancel?: () => void;
  submitting?: boolean;
};

function toSocialLinksForm(
  dto: SettingsDTO["general"]["socialLinks"],
): SettingsFormValues["general"]["socialLinks"] {
  const first = dto?.[0];
  return {
    facebook: first?.facebook ?? "",
    tiktok: first?.tiktok ?? "",
    instagram: first?.instagram ?? "",
    twitter: first?.twitter ?? "",
    youtube: first?.youtube ?? "",
  };
}

function formValuesFromDto(dto: SettingsDTO): SettingsFormValues {
  return {
    general: {
      logoKey: dto.general.logoKey ?? "",
      siteName: dto.general.siteName ?? { lo: "", th: "", en: "" },
      slogan: dto.general.slogan ?? { lo: "", th: "", en: "" },
      address: dto.general.address ?? { lo: "", th: "", en: "" },
      phone: dto.general.phone ?? "",
      email: dto.general.email ?? "",
      socialLinks: toSocialLinksForm(dto.general.socialLinks),
    },
    seo: {
      title: dto.seo.title ?? { lo: "", th: "", en: "" },
      description: dto.seo.description ?? { lo: "", th: "", en: "" },
      imageKey: dto.seo.imageKey ?? "",
    },
    codEnabled: dto.paymentMethods.methods.includes("cash-on-delivery"),
    phajayEnabled: dto.paymentMethods.methods.includes("phajay"),
  };
}

function formValuesToInput(values: SettingsFormValues): SettingsUpdateInput {
  const methods: ("phajay" | "cash-on-delivery")[] = [];
  if (values.codEnabled) methods.push("cash-on-delivery");
  if (values.phajayEnabled) methods.push("phajay");
  const sl = values.general.socialLinks;
  const socialLinks =
    sl.facebook || sl.tiktok || sl.instagram || sl.twitter || sl.youtube
      ? [
          {
            facebook: sl.facebook || undefined,
            tiktok: sl.tiktok || undefined,
            instagram: sl.instagram || undefined,
            twitter: sl.twitter || undefined,
            youtube: sl.youtube || undefined,
          },
        ]
      : [];
  return {
    general: {
      logoKey: values.general.logoKey,
      siteName: values.general.siteName,
      slogan: values.general.slogan,
      address: values.general.address,
      phone: values.general.phone,
      email: values.general.email,
      socialLinks,
    },
    seo: {
      title: values.seo.title,
      description: values.seo.description,
      keywords: [],
      imageKey: values.seo.imageKey,
    },
    paymentMethods: {
      enabled: methods.length > 0,
      methods,
    },
  };
}

const LOCALES = [
  { value: "lo", label: "LO" },
  { value: "th", label: "TH" },
  { value: "en", label: "EN" },
] as const;

export function SettingsForm({
  initialValues,
  onSubmit,
  onCancel,
  submitting,
}: SettingsFormProps) {
  const [pendingOgFile, setPendingOgFile] = useState<File | null>(null);
  const [pendingLogoFile, setPendingLogoFile] = useState<File | null>(null);
  const [uploadingOg, setUploadingOg] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);

  const methods = RHF.useForm<SettingsFormValues>({
    resolver: zodResolver(FormSchema) as Resolver<SettingsFormValues>,
    defaultValues: formValuesFromDto(initialValues),
  });

  const handleSubmit = useCallback(
    async (vals: SettingsFormValues) => {
      let finalImageKey = vals.seo.imageKey;
      let finalLogoKey = vals.general.logoKey;
      if (pendingLogoFile) {
        setUploadingLogo(true);
        try {
          const blob = await compressToWebP(pendingLogoFile);
          const baseName = pendingLogoFile.name.includes(".")
            ? pendingLogoFile.name.slice(
                0,
                pendingLogoFile.name.lastIndexOf("."),
              )
            : pendingLogoFile.name;
          const key = `${OG_KEY_PREFIX}/logo-${Date.now()}-${sanitizeFilename(baseName)}.webp`;
          const contentType = "image/webp";
          const { uploadUrl } = await uploadApi.getPresignUrl({
            key,
            contentType,
          });
          await uploadFileToPresignedUrl(uploadUrl, blob, { contentType });
          finalLogoKey = key;
          setPendingLogoFile(null);
        } finally {
          setUploadingLogo(false);
        }
      }
      if (pendingOgFile) {
        setUploadingOg(true);
        try {
          finalImageKey = await uploadOgImageToKey(pendingOgFile);
          setPendingOgFile(null);
        } finally {
          setUploadingOg(false);
        }
      }
      const payload: SettingsFormValues = {
        ...vals,
        general: { ...vals.general, logoKey: finalLogoKey },
        seo: { ...vals.seo, imageKey: finalImageKey },
      };
      await onSubmit(formValuesToInput(payload));
    },
    [onSubmit, pendingOgFile, pendingLogoFile],
  );

  const handleClearOg = useCallback(() => {
    setPendingOgFile(null);
    methods.setValue("seo.imageKey", "");
  }, [methods]);

  const handleClearLogo = useCallback(() => {
    setPendingLogoFile(null);
    methods.setValue("general.logoKey", "");
  }, [methods]);

  return (
    <FormRoot<SettingsFormValues>
      methods={methods as UseFormReturn<SettingsFormValues>}
      onSubmit={handleSubmit}
    >
      <div className="space-y-10">
        {/* ຕັ້ງຄ່າທົ່ວໄປ (General) — logo, siteName, slogan, address, contact, social */}
        <section className="space-y-4 rounded-lg border p-6">
          <h3 className="font-semibold text-muted-foreground text-sm uppercase tracking-wide">
            ຕັ້ງຄ່າທົ່ວໄປ
          </h3>
          <div className="grid gap-6 pt-2 lg:grid-cols-2">
            <div className="space-y-4">
              <RHF.Controller
                name="general.logoKey"
                control={methods.control}
                render={({ field }) => (
                  <ImageKeyUploadField
                    label="ໂລໂກ້ (Logo)"
                    value={field.value}
                    onChange={field.onChange}
                    keyPrefix={OG_KEY_PREFIX}
                    aspectRatio="aspect-square"
                    widthPx={512}
                    heightPx={512}
                    className="max-w-48"
                    deferUpload
                    pendingFile={pendingLogoFile}
                    onFileSelect={setPendingLogoFile}
                    onClear={handleClearLogo}
                  />
                )}
              />

              <Tabs defaultValue="lo" className="w-full">
                <TabsList className="h-9">
                  {LOCALES.map(({ value, label }) => (
                    <TabsTrigger
                      key={value}
                      value={value}
                      className="px-3 text-xs"
                    >
                      {label}
                    </TabsTrigger>
                  ))}
                </TabsList>
                {LOCALES.map((locale) => (
                  <TabsContent
                    key={locale.value}
                    value={locale.value}
                    className="mt-4 space-y-4"
                  >
                    <FormInput
                      name={`general.siteName.${locale.value}`}
                      label={`ຊື່ເວັບ (${locale.label})`}
                      placeholder="ປ້ອນຊື່ເວັບ"
                    />
                    <FormInput
                      name={`general.slogan.${locale.value}`}
                      label={`ຄຳຂວັນ (${locale.label})`}
                      placeholder="ຄຳຂວັນສັ້ນໆ"
                    />
                    <FormTextarea
                      name={`general.address.${locale.value}`}
                      label={`ທີ່ຢູ່ (${locale.label})`}
                      placeholder="ທີ່ຢູ່ສຳເລັດ"
                      rows={2}
                    />
                  </TabsContent>
                ))}
              </Tabs>
            </div>
            <div className="space-y-4">
              <FormInput
                name="general.phone"
                label="ເບີໂທ"
                placeholder="020 xxx xxxxx"
              />
              <FormInput
                name="general.email"
                label="ອີເມວ"
                placeholder="contact@example.com"
              />
              <div className="space-y-2">
                <p className="font-medium text-muted-foreground text-sm">
                  ລິ້ງສື່ສັງຄົມ
                </p>
                <FormInput
                  name="general.socialLinks.facebook"
                  label="Facebook"
                  placeholder="https://..."
                />
                <FormInput
                  name="general.socialLinks.tiktok"
                  label="TikTok"
                  placeholder="https://..."
                />
                <FormInput
                  name="general.socialLinks.instagram"
                  label="Instagram"
                  placeholder="https://..."
                />
                <FormInput
                  name="general.socialLinks.twitter"
                  label="X (Twitter)"
                  placeholder="https://..."
                />
                <FormInput
                  name="general.socialLinks.youtube"
                  label="YouTube"
                  placeholder="https://..."
                />
              </div>
            </div>
          </div>
        </section>

        {/* ຕັ້ງຄ່າ SEO — title, description, imageKey */}
        <section className="space-y-4 rounded-lg border p-6">
          <Tabs defaultValue="lo" className="w-full">
            <div className="flex flex-wrap items-center justify-between gap-4">
              <h3 className="font-semibold text-muted-foreground text-sm uppercase tracking-wide">
                ຕັ້ງຄ່າ SEO ທົ່ວໂລກ
              </h3>
              <TabsList className="h-9">
                {LOCALES.map(({ value, label }) => (
                  <TabsTrigger
                    key={value}
                    value={value}
                    className="px-3 text-xs"
                  >
                    {label}
                  </TabsTrigger>
                ))}
              </TabsList>
            </div>
            <div className="grid gap-6 pt-4 lg:grid-cols-2">
              <div className="space-y-4">
                {LOCALES.map((locale) => (
                  <TabsContent
                    key={locale.value}
                    value={locale.value}
                    className="mt-0 space-y-4"
                  >
                    <FormInput
                      name={`seo.title.${locale.value}`}
                      label={`ຫົວຂໍ້ meta ເລີ່ມຕົ້ນ (${locale.label})`}
                      placeholder="ຫົວຂໍ້ທີ່ເກີດໃນເຄື່ອງມືຄົ້ນຫາ"
                    />
                    <FormTextarea
                      name={`seo.description.${locale.value}`}
                      label={`ຄຳອະທິບາຍ meta (${locale.label})`}
                      placeholder="ອະທິບາຍຮ້ານຂອງທ່ານໃຫ້ເຄື່ອງມືຄົ້ນຫາ..."
                      rows={3}
                    />
                  </TabsContent>
                ))}
              </div>
              <div className="space-y-6 lg:pl-2">
                <RHF.Controller
                  name="seo.imageKey"
                  control={methods.control}
                  render={({ field }) => (
                    <ImageKeyUploadField
                      label="ຮູບ Open Graph (1200×630)"
                      value={field.value}
                      onChange={field.onChange}
                      keyPrefix={OG_KEY_PREFIX}
                      aspectRatio="aspect-video"
                      widthPx={1200}
                      heightPx={630}
                      className="w-full"
                      deferUpload
                      pendingFile={pendingOgFile}
                      onFileSelect={setPendingOgFile}
                      onClear={handleClearOg}
                    />
                  )}
                />
                <p className="mt-1 text-muted-foreground text-xs">
                  ຮູບທີ່ສະແດງເມື່ອເວັບຖືກແບ່ງປັນໃນເຟສບຸກ ຫຼື X.
                </p>
              </div>
            </div>
          </Tabs>
        </section>

        {/* ວິທີການຊຳລະ — ใช้ Toggle (FormSwitch) */}
        <section className="space-y-4 rounded-lg border p-6">
          <h3 className="font-semibold text-muted-foreground text-sm uppercase tracking-wide">
            ວິທີການຊຳລະ
          </h3>
          <div className="space-y-6">
            <div className="flex flex-wrap items-center justify-between gap-4 rounded border p-4">
              <div className="flex items-start gap-3">
                <Truck className="mt-0.5 size-5 text-muted-foreground" />
                <div>
                  <p className="font-medium">ຊຳລະປາຍທາງ (COD)</p>
                  <p className="text-muted-foreground text-sm">
                    ອະນຸຍາດໃຫ້ລູກຄ້າຊຳລະເງິນສົດເມື່ອຮັບພັກສັງທີ່ປະຕູເຮືອນ.
                  </p>
                </div>
              </div>
              <div className="w-fit">
                <FormSwitch name="codEnabled" />
              </div>
            </div>
            <div className="flex flex-wrap items-center justify-between gap-4 rounded border p-4">
              <div className="flex items-start gap-3">
                <CreditCard className="mt-0.5 size-5 text-muted-foreground" />
                <div>
                  <p className="font-medium">ຊຳລະອອນໄລນ໌ (PhaJay)</p>
                  <p className="text-muted-foreground text-sm">
                    ປະຕູທາງທ້ອງຖິ່ນສຳລັບບັດເຄຣດິດ ແລະ ການຊຳລະຜ່ານ QR ຢ່າງປອດໄພ.
                  </p>
                </div>
              </div>
              <div className="w-fit">
                <FormSwitch name="phajayEnabled" />
              </div>
            </div>
          </div>
        </section>

        {/* ເຂດອັນຕະລາກ */}
        {/* <section className="space-y-4 rounded-lg border border-destructive/50 p-6">
          <h3 className="font-semibold text-destructive text-sm uppercase tracking-wide">
            ເຂດອັນຕະລາກ
          </h3>
          <div className="flex flex-wrap items-center justify-between gap-4 rounded border border-destructive/30 bg-destructive/5 p-4">
            <div>
              <p className="font-medium">ໂໝດບຳລຸງຮັກສາ</p>
              <p className="text-muted-foreground text-sm">
                ປິດຫນ້າຮ້ານຊົ່ວຄາວໃນຂະນະມີການອັບເດດ.
              </p>
            </div>
            <Button type="button" variant="destructive" disabled>
              ເປີດໃຊ້ງານ
            </Button>
          </div>
        </section> */}

        {/* ປຸ່ມດ້ານລຸ່ມ */}
        <div className="flex flex-wrap items-center gap-3 border-t pt-6">
          <Button
            type="submit"
            disabled={submitting || uploadingOg || uploadingLogo}
          >
            {uploadingOg || uploadingLogo ? "ກຳລັງອັບໂຫຼດຮູບ..." : "ບັນທຶກການປ່ຽນແປງ"}
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={() => {
              setPendingOgFile(null);
              setPendingLogoFile(null);
              methods.reset(formValuesFromDto(initialValues));
              onCancel?.();
            }}
          >
            ຍົກເລີກ
          </Button>
        </div>
      </div>
    </FormRoot>
  );
}
